'use client';

import { PropsWithChildren, useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { getUniqueId } from '@micro-apps/env';
import { cn } from '@fshr/shared-react';
import RadioButtonComponent, { Option } from '../radio-button/radio-button';

export type Props = {
	options?: Option[];
	id?: string;
	className?: string;
	defaultValue?: string;
	onChange?: (event: React.ChangeEvent<HTMLInputElement>, option: Option) => void;
	internalRadioButtonClassName?: string;
	externalRadioButtonClassName?: string;
	labelContainerClassName?: string;
	labelTextClassName?: string;
	radioButtonLabelClassName?: string;
	radioButtonContainerClassName?: string;
	cirlceContainerClassName?: string;
	externalCircleCheckedClassNames?: string[];
	internalCircleCheckedClassNames?: string[];
	detailContainerClassName?: string;
};

export function RadioGroup({
	id = `${getUniqueId()}-RadioButtons`,
	options,
	className,
	defaultValue,
	onChange,
	internalRadioButtonClassName,
	externalRadioButtonClassName,
	labelContainerClassName,
	labelTextClassName,
	radioButtonLabelClassName,
	radioButtonContainerClassName,
	cirlceContainerClassName,
	externalCircleCheckedClassNames,
	internalCircleCheckedClassNames,
	detailContainerClassName,
}: PropsWithChildren<Props>): ReactElement {
	const [checkedValue, setCheckedValue] = useState<string>('');

	// Set initial checked value based on defaultValue or first option
	useEffect(() => {
		if (options && options.length > 0) {
			if (defaultValue && options.some(option => option.value === defaultValue)) {
				setCheckedValue(defaultValue);
			} else {
				setCheckedValue(options[0].value);
			}
		}
	}, []);

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>, option: Option) => {
		setCheckedValue(option.value);
		onChange && onChange(event, option);
	};

	return (
		<div
			role="radiogroup"
			id={id}
			className={cn('flex flex-col lg:flex-row lg:flex-wrap lg:gap-5 uppercase', className)}
		>
			{options &&
				options.length &&
				options.map((option, i) => (
					<RadioButtonComponent
						key={`${id}-radio-button-${i}-${option.value}`}
						option={option}
						id={id}
						checked={checkedValue === option.value}
						onChange={handleChange}
						radioButtonLabelClassName={radioButtonLabelClassName}
						internalRadioButtonClassName={internalRadioButtonClassName}
						externalRadioButtonClassName={externalRadioButtonClassName}
						labelContainerClassName={labelContainerClassName}
						labelTextClassName={labelTextClassName}
						radioButtonContainerClassName={radioButtonContainerClassName}
						cirlceContainerClassName={cirlceContainerClassName}
						externalCircleCheckedClassNames={externalCircleCheckedClassNames}
						internalCircleCheckedClassNames={internalCircleCheckedClassNames}
						detailContainerClassName={detailContainerClassName}
					/>
				))}
		</div>
	);
}
